<div class="modal-header">
    <h5 class="modal-title"><i class="fa-regular fa-database" aria-hidden="true"></i> Tjek databasetabel
        @if (dbTemplateName()) { <small class="text-body-secondary ms-2">{{ dbTemplateName() }}</small> }
    </h5>
    <button type="button" class="btn-close" aria-label="Luk" [disabled]="applying()"
        (click)="activeModal.dismiss('Cross click')"></button>
</div>

<div class="modal-body" style="min-height: 280px;">
    @if (diff.isLoading()) {
    <div class="d-flex flex-column justify-content-center align-items-center w-100" style="height: 240px">
        <div class="spinner-border" role="status"><span class="visually-hidden">Indlæser...</span></div>
        <p class="mt-2 mb-0">Sammenligner skabelonen med databasen...</p>
    </div>
    } @else if (diff.error()) {
    <div class="alert alert-danger" role="alert">
        <h5 class="alert-heading">Kunne ikke tjekke databasetabellen</h5>
        <hr>
        <p class="mb-2">Databaseobjektet kunne ikke læses. Skabelonen er ikke ændret.</p>
        <button type="button" class="btn btn-outline-primary btn-sm" (click)="diff.reload()">Prøv igen</button>
    </div>
    } @else if (diff.value(); as diff) {
    @if (!diff.objectExists) {
    <div class="alert alert-warning" role="alert">
        <h5 class="alert-heading">Databaseobjektet findes ikke</h5>
        <p class="mb-0">
            <code>{{ diff.schemaName }}.{{ diff.objectName }}</code> findes ikke længere i databasen. Synkroniseringen vil
            fejle for skabeloner baseret på denne databaseskabelon — overvej at deaktivere den.
        </p>
    </div>
    } @else if (rows().length == 0) {
    <div class="alert alert-success" role="alert">
        <i class="fa-solid fa-circle-check" aria-hidden="true"></i>
        Ingen ændringer — skabelonen svarer til <code>{{ diff.schemaName }}.{{ diff.objectName }}</code>.
    </div>
    } @else {
    <p>
        Forskelle mellem skabelonen og <code>{{ diff.schemaName }}.{{ diff.objectName }}</code>. Vælg, hvad der skal ske
        med hver af dem. Intet ændres, før du trykker <strong>Opdater skabelon</strong>.
    </p>
    @if (!diff.outputColumnsKnown) {
    <div class="alert alert-info" role="note">
        Stored procedurens resultatsæt kunne ikke beskrives, så output-felterne er ikke sammenlignet.
    </div>
    }

    @if (added().length > 0) {
    <h6 class="mt-3">Nye kolonner i databasen ({{ added().length }})</h6>
    <table class="table table-sm align-middle">
        <thead>
            <tr>
                <th scope="col">Kolonne</th>
                <th scope="col">I databasen</th>
                <th scope="col" style="width: 16rem">Handling</th>
            </tr>
        </thead>
        <tbody>
            @for (row of added(); track row.key) {
            <tr>
                <td><code>{{ row.change.columnName }}</code>
                    @if (originName(row.change.origin)) { <small class="text-body-secondary ms-1">{{ originName(row.change.origin) }}</small> }
                </td>
                <td>{{ describe(row.change.database) }}</td>
                <td>
                    @if (isRenameTarget(row)) {
                    <span class="text-body-secondary">Bruges af et omdøbt felt</span>
                    } @else {
                    <select class="form-select form-select-sm" [attr.aria-label]="'Handling for ny kolonne ' + row.change.columnName"
                        [disabled]="applying()" (change)="setChoice(row, $event)">
                        <option value="apply" [selected]="row.choice() == 'apply'">Tilføj</option>
                        <option value="hidden" [selected]="row.choice() == 'hidden'">Tilføj skjult (ikke i afdelingernes skabeloner)</option>
                        <option value="ignore" [selected]="row.choice() == 'ignore'">Ignorér</option>
                    </select>
                    }
                </td>
            </tr>
            }
        </tbody>
    </table>
    }

    @if (removed().length > 0) {
    <h6 class="mt-3">Felter hvis kolonne ikke længere findes ({{ removed().length }})</h6>
    <table class="table table-sm align-middle">
        <thead>
            <tr>
                <th scope="col">Felt</th>
                <th scope="col">I skabelonen</th>
                <th scope="col">Bruges i</th>
                <th scope="col" style="width: 16rem">Handling</th>
            </tr>
        </thead>
        <tbody>
            @for (row of removed(); track row.key) {
            <tr>
                <td><code>{{ row.change.columnName }}</code>
                    @if (row.change.fieldName && row.change.fieldName != row.change.columnName) { <span class="ms-1">[{{ row.change.fieldName }}]</span> }
                    @if (originName(row.change.origin)) { <small class="text-body-secondary ms-1">{{ originName(row.change.origin) }}</small> }
                </td>
                <td>{{ describe(row.change.template) }}</td>
                <td>
                    {{ row.change.usedInTemplates }} skabelon(er)
                    @if (row.change.usedInFilters > 0) {
                    <div class="text-danger small">{{ row.change.usedInFilters }} filter(e) — kan ikke fjernes</div>
                    }
                </td>
                <td>
                    <select class="form-select form-select-sm" [attr.aria-label]="'Handling for fjernet felt ' + row.change.columnName"
                        [disabled]="applying()" (change)="setChoice(row, $event)">
                        <option value="apply" [disabled]="row.change.usedInFilters > 0" [selected]="row.choice() == 'apply'">Fjern</option>
                        <option value="ignore" [selected]="row.choice() == 'ignore'">Behold</option>
                        @for (target of renameOptions(row); track target.columnName) {
                        <option [value]="'rename:' + target.columnName" [selected]="row.choice() == 'rename:' + target.columnName">Omdøbt til {{ target.columnName }}</option>
                        }
                    </select>
                </td>
            </tr>
            }
        </tbody>
    </table>
    }

    @if (modified().length > 0) {
    <h6 class="mt-3">Ændrede kolonner ({{ modified().length }})</h6>
    <table class="table table-sm align-middle">
        <thead>
            <tr>
                <th scope="col">Felt</th>
                <th scope="col">I skabelonen</th>
                <th scope="col">I databasen</th>
                <th scope="col" style="width: 16rem">Handling</th>
            </tr>
        </thead>
        <tbody>
            @for (row of modified(); track row.key) {
            <tr>
                <td><code>{{ row.change.columnName }}</code>
                    @if (row.change.fieldName && row.change.fieldName != row.change.columnName) { <span class="ms-1">[{{ row.change.fieldName }}]</span> }
                </td>
                <td>{{ describe(row.change.template) }}</td>
                <td>{{ describe(row.change.database) }}</td>
                <td>
                    <select class="form-select form-select-sm" [attr.aria-label]="'Handling for ændret felt ' + row.change.columnName"
                        [disabled]="applying()" (change)="setChoice(row, $event)">
                        <option value="apply" [selected]="row.choice() == 'apply'">Opdatér</option>
                        <option value="ignore" [selected]="row.choice() == 'ignore'">Ignorér</option>
                    </select>
                    @if (row.offerMandatory && row.choice() == 'apply') {
                    <div class="form-check mt-1">
                        <input class="form-check-input" type="checkbox" [id]="'mandatory-' + row.key"
                            [disabled]="applying()" [checked]="row.makeMandatory()" (change)="setMakeMandatory(row, $event)">
                        <label class="form-check-label small" [for]="'mandatory-' + row.key">Gør obligatorisk (kolonnen er nu NOT NULL)</label>
                    </div>
                    }
                </td>
            </tr>
            }
        </tbody>
    </table>
    }

    @if (summary().removedFromTemplates > 0) {
    <div class="alert alert-warning mb-0" role="note">
        De fjernede felter tages også ud af afdelingernes skabeloner ({{ summary().removedFromTemplates }} i alt). Det
        skrives i afdelingernes log.
    </div>
    }
    }
    }
</div>

<div class="modal-footer">
    @if (rows().length > 0) {
    <span class="me-auto text-body-secondary small">
        {{ summary().added }} tilføjes, {{ summary().removed }} fjernes, {{ summary().renamed }} omdøbes, {{ summary().modified }} opdateres
    </span>
    }
    <button type="button" class="btn btn-secondary" [disabled]="applying()" (click)="activeModal.dismiss('Cancel')">
        {{ rows().length > 0 ? 'Annuller' : 'Luk' }}
    </button>
    @if (rows().length > 0) {
    <button type="button" class="btn btn-success" [disabled]="applying() || nothingChosen()" (click)="apply()">
        @if (applying()) {
        <span class="spinner-border spinner-border-sm me-2" role="status"><span class="visually-hidden">Opdaterer...</span></span>
        }
        Opdater skabelon
    </button>
    }
</div>
